import { useEffect, useState, useMemo } from 'react';
import {
  CheckCircle,
  Circle,
  Loader2,
  Activity,
  Shield,
  Brain,
  TrendingUp,
  AlertTriangle,
  BarChart2,
  Zap,
} from '../../icons';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import type { ChartConfig } from '../ui/chart';

const agentsChartConfig: ChartConfig = {
  v: { label: 'Score', color: '#6366F1' },
};

export interface ProcessingPanelProps {
  agentType: 'brandsafety' | 'script' | 'video';
  elapsedSecs: number;
}

interface Stage {
  label: string;
  detail: string;
  durationSecs: number;
}

const STAGES: Record<ProcessingPanelProps['agentType'], Stage[]> = {
  brandsafety: [
    {
      label: 'Authenticating & fetching channel data',
      detail: 'Resolving channel identity and metadata',
      durationSecs: 2,
    },
    {
      label: 'Scanning recent videos',
      detail: 'Sampling latest uploads for risk signals',
      durationSecs: 4,
    },
    {
      label: 'Extracting comments & engagement data',
      detail: 'Pulling top comments, replies and sentiment signals',
      durationSecs: 6,
    },
    {
      label: 'Running AI safety classification',
      detail: 'Checking content against brand-safety policy vectors',
      durationSecs: 8,
    },
    {
      label: 'Analysing transcript sentiment',
      detail: 'Scoring speech tone, controversial topics and risk flags',
      durationSecs: 10,
    },
    {
      label: 'Cross-referencing advertiser guidelines',
      detail: 'Mapping findings to GARM / IAB suitability tiers',
      durationSecs: 12,
    },
    {
      label: 'Compiling safety report',
      detail: 'Generating scores, recommendations and red-flag summary',
      durationSecs: 999,
    },
  ],
  script: [
    {
      label: 'Parsing script structure',
      detail: 'Segmenting intro, body, call-to-action and outro',
      durationSecs: 3,
    },
    {
      label: 'Checking disclosure compliance',
      detail: 'Validating FTC / ASA sponsorship disclosure language',
      durationSecs: 8,
    },
    {
      label: 'Analysing brand voice alignment',
      detail: 'Comparing tone against brand brief guidelines',
      durationSecs: 14,
    },
    {
      label: 'Flagging policy violations',
      detail: 'Scanning for restricted claims and risk terms',
      durationSecs: 22,
    },
    {
      label: 'Scoring audience suitability',
      detail: 'Rating content for age-gating and platform policy',
      durationSecs: 30,
    },
    {
      label: 'Generating improvement suggestions',
      detail: 'Drafting alternative phrasings and compliance fixes',
      durationSecs: 999,
    },
  ],
  video: [
    {
      label: 'Retrieving video metadata',
      detail: 'Fetching title, description, tags and engagement metrics',
      durationSecs: 4,
    },
    {
      label: 'Extracting and cleaning transcript',
      detail: 'Processing captions and auto-generated speech text',
      durationSecs: 10,
    },
    {
      label: 'Performing sentiment analysis',
      detail: 'Scoring emotional tone across video timeline segments',
      durationSecs: 18,
    },
    {
      label: 'Detecting brand mention moments',
      detail: 'Timestamping key brand references and CTA delivery',
      durationSecs: 26,
    },
    {
      label: 'Evaluating audience response signals',
      detail: 'Cross-referencing comments with sentiment peaks',
      durationSecs: 35,
    },
    {
      label: 'Building performance scorecard',
      detail: 'Summarising reach, resonance and risk indicators',
      durationSecs: 999,
    },
  ],
};

const TIPS: Record<ProcessingPanelProps['agentType'], string[]> = {
  brandsafety: [
    'Brand-safe channels reduce ad recall drop-off by up to 40%.',
    'GARM tier 1 & 2 content reaches 92% of brand-safe inventory.',
    'Channels with <2% flagged comments score highest for CPM efficiency.',
    'Transcript sentiment is a stronger predictor than comment sentiment alone.',
  ],
  script: [
    'Scripts reviewed by AI reduce compliance re-shoots by 65%.',
    'Clear disclosure language increases trust scores by 22% on average.',
    'Top-performing scripts front-load the brand mention within 30 seconds.',
    'Conversational tone outperforms formal scripts by 3× on engagement.',
  ],
  video: [
    'Videos with mid-roll brand mentions outperform end-card placements by 2×.',
    'Positive sentiment peaks within the first 60 seconds drive highest recall.',
    'Comment sentiment analysis is 78% predictive of future video performance.',
    'Transcripts with 3+ brand mentions correlate with 18% higher recall lift.',
  ],
};

// Seeded pseudo-random to generate deterministic but varied data
function seededRand(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

function buildAreaData(elapsedSecs: number, agentType: string) {
  const rand = seededRand(42);
  const points = [];
  const count = Math.min(elapsedSecs + 1, 40);
  for (let i = 0; i < count; i++) {
    const progress = i / Math.max(count - 1, 1);
    const noise = rand() * 8 - 4;
    let value: number;
    if (agentType === 'brandsafety') {
      value = Math.max(0, Math.min(100, 20 + progress * 55 + noise));
    } else if (agentType === 'script') {
      value = Math.max(0, Math.min(100, 30 + progress * 50 + noise));
    } else {
      value = Math.max(0, Math.min(100, 25 + progress * 60 + noise));
    }
    points.push({ t: i, v: Math.round(value) });
  }
  return points;
}

function buildRadarData(elapsedSecs: number, activeIdx: number, stagesLen: number) {
  const progress = Math.min(activeIdx / (stagesLen - 1), 1);
  const rand = seededRand(elapsedSecs * 7 + 13);
  const metrics = [
    { subject: 'Authenticity', base: 68 },
    { subject: 'Safety', base: 72 },
    { subject: 'Compliance', base: 61 },
    { subject: 'Sentiment', base: 75 },
    { subject: 'Views', base: 58 },
    { subject: 'Engagement', base: 64 },
  ];
  return metrics.map((m) => ({
    subject: m.subject,
    score: Math.round(m.base * progress + rand() * 8),
  }));
}

function buildBarData(elapsedSecs: number) {
  const rand = seededRand(elapsedSecs + 99);
  return [
    { name: 'Safe', value: Math.round(50 + rand() * 30), color: '#22c55e' },
    { name: 'Caution', value: Math.round(15 + rand() * 15), color: '#f59e0b' },
    { name: 'Flagged', value: Math.round(5 + rand() * 10), color: '#ef4444' },
    { name: 'Review', value: Math.round(10 + rand() * 12), color: '#171717' },
  ];
}

function buildLiveCounters(elapsedSecs: number, agentType: string) {
  const rand = seededRand(elapsedSecs * 3 + 7);
  if (agentType === 'brandsafety') {
    return [
      { label: 'Videos Scanned', value: Math.min(elapsedSecs * 2, 24), icon: '🎬' },
      { label: 'Comments Checked', value: Math.min(elapsedSecs * 5, 180), icon: '💬' },
      {
        label: 'Risk Signals',
        value: Math.min(Math.round(elapsedSecs * 0.3 + rand() * 1), 8),
        icon: '⚠️',
      },
      { label: 'Policy Vectors', value: Math.min(elapsedSecs * 2, 48), icon: '📋' },
    ];
  } else if (agentType === 'script') {
    return [
      { label: 'Segments Parsed', value: Math.min(elapsedSecs * 3, 48), icon: '📝' },
      { label: 'Claims Scanned', value: Math.min(elapsedSecs * 8, 320), icon: '🔍' },
      { label: 'Violations Found', value: Math.min(Math.round(elapsedSecs * 0.2), 6), icon: '🚩' },
      { label: 'Suggestions Ready', value: Math.min(elapsedSecs, 24), icon: '💡' },
    ];
  } else {
    return [
      { label: 'Frames Analysed', value: Math.min(elapsedSecs * 120, 14400), icon: '🎞️' },
      { label: 'Transcript Words', value: Math.min(elapsedSecs * 22, 2200), icon: '📄' },
      { label: 'Brand Mentions', value: Math.min(Math.round(elapsedSecs * 0.3), 12), icon: '🏷️' },
      { label: 'Sentiment Points', value: Math.min(elapsedSecs * 5, 480), icon: '📊' },
    ];
  }
}

const PULSE_COLORS: Record<string, string> = {
  brandsafety: '#171717',
  script: '#0ea5e9',
  video: '#f59e0b',
};

// Animated number that increments toward a target
function AnimatedNumber({ target }: { target: number }) {
  const [displayed, setDisplayed] = useState(0);
  useEffect(() => {
    if (displayed < target) {
      const diff = target - displayed;
      const step = Math.max(1, Math.floor(diff / 6));
      const id = setTimeout(() => setDisplayed((d) => Math.min(d + step, target)), 50);
      return () => clearTimeout(id);
    }
  }, [target, displayed]);
  return <>{displayed.toLocaleString()}</>;
}

export default function ProcessingPanel({ agentType, elapsedSecs }: ProcessingPanelProps) {
  const stages = STAGES[agentType];
  const tips = TIPS[agentType];
  const [tipIdx, setTipIdx] = useState(0);
  const [dotCount, setDotCount] = useState(1);
  const [pulse, setPulse] = useState(false);
  const accent = PULSE_COLORS[agentType];

  useEffect(() => {
    const id = setInterval(() => setTipIdx((i) => (i + 1) % tips.length), 5000);
    return () => clearInterval(id);
  }, [tips.length]);

  useEffect(() => {
    const id = setInterval(() => setDotCount((d) => (d % 3) + 1), 500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setPulse((p) => !p), 1200);
    return () => clearInterval(id);
  }, []);

  let cumulativeSecs = 0;
  let activeIdx = stages.length - 1;
  for (let i = 0; i < stages.length; i++) {
    cumulativeSecs += stages[i]?.durationSecs ?? 0;
    if (elapsedSecs < cumulativeSecs) {
      activeIdx = i;
      break;
    }
  }

  const progressPct = Math.round((activeIdx / (stages.length - 1)) * 100);
  const dots = '.'.repeat(dotCount);
  const mins = Math.floor(elapsedSecs / 60);
  const secs = elapsedSecs % 60;
  const elapsed = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  const agentLabel =
    agentType === 'brandsafety'
      ? 'Brand Safety'
      : agentType === 'script'
        ? 'Script Review'
        : 'Video Analysis';

  const areaData = useMemo(() => buildAreaData(elapsedSecs, agentType), [elapsedSecs, agentType]);
  const radarData = useMemo(
    () => buildRadarData(elapsedSecs, activeIdx, stages.length),
    [elapsedSecs, activeIdx, stages.length],
  );
  const barData = useMemo(() => buildBarData(elapsedSecs), [elapsedSecs]);
  const counters = useMemo(
    () => buildLiveCounters(elapsedSecs, agentType),
    [elapsedSecs, agentType],
  );

  const AgentIcon =
    agentType === 'brandsafety' ? Shield : agentType === 'script' ? Brain : Activity;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '32px 24px',
        gap: 20,
        minHeight: 480,
        background: 'linear-gradient(145deg, #fafaf9 0%, #f0f9ff 50%, #fff7ed 100%)',
      }}
    >
      {/* ── HEADER ── */}
      <div style={{ textAlign: 'center', maxWidth: 680, width: '100%' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            background: `linear-gradient(135deg, ${accent}, #0ea5e9)`,
            borderRadius: 999,
            padding: '8px 22px',
            marginBottom: 12,
            
          }}
        >
          <AgentIcon size={15} color="#fff" />
          <span
            style={{
              fontSize: 11,
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            {agentLabel} AI • Running{dots}
          </span>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: pulse ? '#fff' : 'rgba(255,255,255,0.4)',
              display: 'inline-block',
              transition: 'background 0.4s',
            }}
          />
        </div>
        <h1
          style={{
            fontSize: 28,
            fontWeight: 900,
            color: '#171717',
            margin: 0,
            letterSpacing: '-0.03em',
          }}
        >
          Deep analysis in progress
        </h1>
        <p style={{ fontSize: 13, color: '#737373', margin: '6px 0 0 0' }}>
          Elapsed: <strong style={{ color: accent }}>{elapsed}</strong>
          <span style={{ margin: '0 10px', opacity: 0.3 }}>|</span>
          Stage {activeIdx + 1} of {stages.length}
          <span style={{ margin: '0 10px', opacity: 0.3 }}>|</span>
          <span style={{ color: '#22c55e', fontWeight: 700 }}>{progressPct}% complete</span>
        </p>
      </div>

      {/* ── PROGRESS BAR ── */}
      <div style={{ width: '100%', maxWidth: 680 }}>
        <div style={{ height: 6, background: '#ecebe9', borderRadius: 999, overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              borderRadius: 999,
              background: `linear-gradient(90deg, ${accent}, #0ea5e9)`,
              width: `${progressPct}%`,
              transition: 'width 1s ease',
              
            }}
          />
        </div>
      </div>

      {/* ── LIVE COUNTER CHIPS ── */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          justifyContent: 'center',
          width: '100%',
          maxWidth: 680,
        }}
      >
        {counters.map((c, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: '#fff',
              border: `1px solid ${accent}30`,
              borderRadius: 12,
              padding: '8px 16px',
              
              flex: '1 1 140px',
              minWidth: 130,
              maxWidth: 160,
            }}
          >
            <span style={{ fontSize: 18 }}>{c.icon}</span>
            <div>
              <div style={{ fontSize: 18, fontWeight: 900, color: accent, lineHeight: 1 }}>
                <AnimatedNumber target={c.value} />
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: '#d4d4d4',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {c.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── THREE-COLUMN CHARTS ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 16,
          width: '100%',
          maxWidth: 960,
        }}
      >
        {/* Chart 1: Analysis Score over time (Area) */}
        <div
          style={{
            background: '#fff',
            borderRadius: 14,
            border: '1px solid #ecebe9',
            padding: '14px 16px',
            
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <TrendingUp size={13} color={accent} />
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: '#5c574f',
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
              }}
            >
              Analysis Score
            </span>
          </div>
          <ChartContainer config={agentsChartConfig} className="h-[100px] w-full">
            <AreaChart data={areaData} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
              <defs>
                <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={accent} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="t" hide />
              <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Area
                isAnimationActive={false}
                type="monotone"
                dataKey="v"
                stroke={accent}
                strokeWidth={2}
                fill="url(#scoreGrad)"
                dot={false}
              />
            </AreaChart>
          </ChartContainer>
        </div>

        {/* Chart 2: Radar (multi-dimension signals) */}
        <div
          style={{
            background: '#fff',
            borderRadius: 14,
            border: '1px solid #ecebe9',
            padding: '14px 16px',
            
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <Zap size={13} color="#0ea5e9" />
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: '#5c574f',
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
              }}
            >
              Signal Radar
            </span>
          </div>
          <ChartContainer config={agentsChartConfig} className="h-[108px] w-full">
            <RadarChart data={radarData}>
              <PolarGrid stroke="var(--border)" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 8, fill: 'var(--muted-foreground)' }} />
              <Radar
                isAnimationActive={false}
                dataKey="score"
                stroke={accent}
                fill={accent}
                fillOpacity={0.18}
                strokeWidth={2}
              />
            </RadarChart>
          </ChartContainer>
        </div>

        {/* Chart 3: Content classification bar */}
        <div
          style={{
            background: '#fff',
            borderRadius: 14,
            border: '1px solid #ecebe9',
            padding: '14px 16px',
            
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <BarChart2 size={13} color="#f59e0b" />
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: '#5c574f',
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
              }}
            >
              Content Mix
            </span>
          </div>
          <ChartContainer config={agentsChartConfig} className="h-[100px] w-full">
            <BarChart data={barData} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
              <XAxis dataKey="name" tick={{ fontSize: 9 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar isAnimationActive={false} dataKey="value" radius={[4, 4, 0, 0]}>
                {barData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </div>
      </div>

      {/* ── TWO-COLUMN BOTTOM SECTION ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          width: '100%',
          maxWidth: 960,
        }}
      >
        {/* Stage Pipeline */}
        <div
          style={{
            background: '#fff',
            borderRadius: 14,
            border: '1px solid #ecebe9',
            overflow: 'hidden',
            
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid #f4f4f2',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Loader2 size={13} color={accent} style={{ animation: 'spin 1s linear infinite' }} />
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: '#5c574f',
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
              }}
            >
              Analysis Pipeline
            </span>
          </div>
          {stages.map((stage, idx) => {
            const done = idx < activeIdx;
            const active = idx === activeIdx;
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '9px 14px',
                  borderBottom: idx < stages.length - 1 ? '1px solid #fafaf9' : 'none',
                  background: active
                    ? `linear-gradient(90deg, ${accent}08, transparent)`
                    : 'transparent',
                  transition: 'background 0.4s ease',
                }}
              >
                <div style={{ marginTop: 2, flexShrink: 0 }}>
                  {done ? (
                    <CheckCircle size={14} color="#22c55e" />
                  ) : active ? (
                    <Loader2
                      size={14}
                      color={accent}
                      style={{ animation: 'spin 1s linear infinite' }}
                    />
                  ) : (
                    <Circle size={14} color="#e5e5e5" />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: active ? 700 : done ? 600 : 400,
                      color: done ? '#22c55e' : active ? accent : '#e0dedb',
                      lineHeight: 1.3,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {stage.label}
                    {active && <span style={{ opacity: 0.6 }}>{dots}</span>}
                  </div>
                  {active && (
                    <div style={{ fontSize: 10.5, color: '#737373', marginTop: 2 }}>
                      {stage.detail}
                    </div>
                  )}
                </div>
                {done && (
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: '#16a34a',
                      background: '#dcfce7',
                      borderRadius: 999,
                      padding: '1px 7px',
                      flexShrink: 0,
                    }}
                  >
                    DONE
                  </span>
                )}
                {active && (
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: accent,
                      background: `${accent}15`,
                      borderRadius: 999,
                      padding: '1px 7px',
                      flexShrink: 0,
                    }}
                  >
                    LIVE
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Right column: Live log + tip */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Live log feed */}
          <div
            style={{
              background: '#fafaf9',
              borderRadius: 14,
              border: '1px solid #ecebe9',
              padding: '14px 16px',
              flex: 1,
              
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#d4d4d4',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                marginBottom: 10,
              }}
            >
              Live Log
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {stages
                .slice(0, activeIdx + 1)
                .reverse()
                .slice(0, 5)
                .map((s, i) => {
                  const isDone = i > 0;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span
                        style={{
                          fontSize: 9,
                          color: '#d4d4d4',
                          fontFamily: 'monospace',
                          flexShrink: 0,
                        }}
                      >
                        {String(activeIdx - i).padStart(2, '0')}:
                      </span>
                      <span
                        style={{
                          fontSize: 10.5,
                          color: isDone ? '#d4d4d4' : '#171717',
                          lineHeight: 1.4,
                          fontFamily: 'monospace',
                        }}
                      >
                        {isDone ? `✓ ${s.label}` : `→ ${s.label}${dots}`}
                      </span>
                    </div>
                  );
                })}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: pulse ? '#22c55e' : '#86efac',
                    display: 'inline-block',
                    transition: 'background 0.3s',
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 9.5, color: '#16a34a', fontFamily: 'monospace' }}>
                  system running • no errors detected
                </span>
              </div>
            </div>
          </div>

          {/* Risk signal meters */}
          <div
            style={{
              background: '#fff',
              borderRadius: 14,
              border: '1px solid #ecebe9',
              padding: '12px 16px',
              
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <AlertTriangle size={13} color="#f59e0b" />
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: '#5c574f',
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                }}
              >
                Risk Signals
              </span>
            </div>
            {[
              {
                label: 'Policy Alignment',
                pct: Math.min(40 + activeIdx * 9, 94),
                color: '#22c55e',
              },
              { label: 'Content Safety', pct: Math.min(35 + activeIdx * 8, 88), color: '#0ea5e9' },
              { label: 'Compliance Score', pct: Math.min(30 + activeIdx * 10, 91), color: accent },
            ].map((m, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, color: '#737373', fontWeight: 600 }}>{m.label}</span>
                  <span style={{ fontSize: 11, color: m.color, fontWeight: 800 }}>{m.pct}%</span>
                </div>
                <div style={{ height: 5, background: '#f4f4f2', borderRadius: 999 }}>
                  <div
                    style={{
                      height: '100%',
                      borderRadius: 999,
                      background: m.color,
                      width: `${m.pct}%`,
                      transition: 'width 1s ease',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Did you know tip */}
          <div
            style={{
              background: `linear-gradient(135deg, ${accent}08, #0ea5e908)`,
              border: `1px solid ${accent}20`,
              borderRadius: 12,
              padding: '12px 16px',
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: accent,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                marginBottom: 5,
              }}
            >
              Did you know?
            </div>
            <div style={{ fontSize: 12, color: '#404040', lineHeight: 1.6, fontStyle: 'italic' }}>
              "{tips[tipIdx]}"
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
